"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { toast } from "sonner";

interface OfflineContextType {
  isOnline: boolean;
  isOffline: boolean;
  hasUnsyncedData: boolean;
  setHasUnsyncedData: (value: boolean) => void;
  syncInProgress: boolean;
  setSyncInProgress: (value: boolean) => void;
  pendingOperationsCount: number;
  setPendingOperationsCount: (count: number) => void;
  requestOnlineAccess: () => Promise<boolean>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export function useOfflineContext() {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error("useOfflineContext must be used within OfflineProvider");
  }
  return context;
}

interface OfflineProviderProps {
  children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const [isOnline, setIsOnline] = useState(true);
  const [hasUnsyncedData, setHasUnsyncedData] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [pendingOperationsCount, setPendingOperationsCount] = useState(0);

  const wasOfflineRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const lastOnlineCheckRef = useRef<number>(Date.now());
  const isTransitioningRef = useRef(false);

  // Función para verificar conexión de forma más robusta
  const checkOnlineStatus = useCallback(async () => {
    if (!navigator.onLine) {
      return false;
    }

    // Evitar verificaciones muy frecuentes
    const now = Date.now();
    if (now - lastOnlineCheckRef.current < 5000) {
      return navigator.onLine;
    }

    lastOnlineCheckRef.current = now;

    try {
      // Intentar hacer una petición HEAD al servidor
      const response = await fetch("/api/health", {
        method: "HEAD",
        cache: "no-cache",
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  // Función para solicitar acceso online (útil para operaciones críticas)
  const requestOnlineAccess = useCallback(async (): Promise<boolean> => {
    const online = await checkOnlineStatus();

    if (!online) {
      toast.error("Sin conexión", {
        id: "network-status-operation-blocked",
        description: "Esta operación requiere conexión a internet",
        duration: 5000,
      });
      return false;
    }

    return true;
  }, [checkOnlineStatus]);

  useEffect(() => {
    // Inicializar estado
    setIsOnline(navigator.onLine);

    const handleOnline = async () => {
      // Evitar múltiples transiciones simultáneas
      if (isTransitioningRef.current || isOnline) {
        return;
      }

      isTransitioningRef.current = true;
      console.log("[OfflineContext] Network: Connection detected");

      // Verificar que realmente hay conexión
      const reallyOnline = await checkOnlineStatus();

      if (!reallyOnline) {
        console.log("[OfflineContext] False positive, still offline");
        isTransitioningRef.current = false;
        return;
      }

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Solo mostrar toast si realmente estábamos offline
      if (wasOfflineRef.current) {
        toast.success("Conexión restaurada", {
          id: "network-status-online",
          description: hasUnsyncedData
            ? "Sincronizando datos pendientes..."
            : "La aplicación está nuevamente en línea",
          duration: 3000,
        });
      }

      setIsOnline(true);
      wasOfflineRef.current = false;

      // Prevenir navegación inmediata
      sessionStorage.setItem("networkJustReconnected", "true");
      setTimeout(() => {
        sessionStorage.removeItem("networkJustReconnected");
        isTransitioningRef.current = false;
      }, 2000);
    };

    const handleOffline = () => {
      // Evitar múltiples transiciones simultáneas
      if (isTransitioningRef.current || !isOnline) {
        return;
      }

      isTransitioningRef.current = true;
      console.log("[OfflineContext] Network: Connection lost");
      setIsOnline(false);
      wasOfflineRef.current = true;

      // Timeout para verificar desconexión prolongada
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log("[OfflineContext] Extended offline period");
        if (hasUnsyncedData) {
          toast.info("Modo offline", {
            id: "network-status-extended-offline",
            description: `Tienes ${pendingOperationsCount} operaciones pendientes de sincronizar`,
            duration: 8000,
          });
        }
      }, 30000); // 30 segundos

      // Liberar el lock después de un momento
      setTimeout(() => {
        isTransitioningRef.current = false;
      }, 1000);
    };

    // Verificación periódica de conexión
    const intervalId = setInterval(async () => {
      const online = await checkOnlineStatus();
      if (online !== isOnline) {
        if (online) {
          handleOnline();
        } else {
          handleOffline();
        }
      }
    }, 60000); // Cada minuto

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(intervalId);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [checkOnlineStatus, hasUnsyncedData, pendingOperationsCount, isOnline]);

  // Actualizar estado de datos no sincronizados al cambiar
  useEffect(() => {
    if (hasUnsyncedData && pendingOperationsCount === 0) {
      setHasUnsyncedData(false);
    }
  }, [pendingOperationsCount, hasUnsyncedData]);

  const value: OfflineContextType = {
    isOnline,
    isOffline: !isOnline,
    hasUnsyncedData,
    setHasUnsyncedData,
    syncInProgress,
    setSyncInProgress,
    pendingOperationsCount,
    setPendingOperationsCount,
    requestOnlineAccess,
  };

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
}
